import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Report, useGetScalingSize } from '@ui/Report';

import { HorizontalStackedBarChart } from './components/HorizontalStackedBarChart';
import { EnergyClassBar } from './components/EnergyClassBar';
import { PieChart } from './components/PieChart';
import { EnergyRow } from './components/EnergyRow';
import { OperationalEnergyData } from './hooks/use-operational-energy.hook';
import { Icon } from '@ui/Icon';
import { ColorShades } from '@shared/helpers/color.helpers';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { LifecycleReportText } from '../LifecycleReportText';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { guards } from '@shared/helpers/typeguards.helpers';

const { Page, Text } = Report;

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((theme, { getScalingSize }) => ({
  page: {
    padding: getScalingSize(0),
    height: '100%',
    display: 'grid',
    gridTemplateAreas: `
      "header header"
      "energy consumption"
    `,
    gridTemplateColumns: '30% 1fr',
    gridTemplateRows: 'auto 1fr',
    gap: getScalingSize(4),
  },
  header: {
    gridArea: 'header',
  },
  energyClass: {
    gridArea: 'energy',
  },
  consumptionSection: {
    gridArea: 'consumption',
  },
}));

type ReportOperationalEnergyProps = {
  color: string;
  energyData: OperationalEnergyData;
  gfa: number;
  footer?: React.ReactNode;
};

export const ReportOperationalEnergy: React.FC<
  ReportOperationalEnergyProps
> = ({ color, energyData, gfa, footer }) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize });
  const {
    operationalEnergyData,
    consumptionData,
    generationData,
    energyClassData,
  } = energyData;

  const shadesCount = consumptionData.length + generationData.length;
  const shades = useMemo(
    () => ColorShades.fromHex(color, shadesCount, [40, 90]),
    [color, shadesCount],
  );

  const primaryEnergyScoreData = getDisplayValueData(
    {
      value: energyClassData.primaryEnergyScore,
      unit: 'kWh',
    },
    { divideBy: { unit: 'm^2' } },
  );

  return (
    <Page verticalAlign="stretch" footer={footer}>
      <Box className={classes.page}>
        {/* Header Section */}
        <Box
          className={classes.header}
          sx={{
            display: 'grid',

            gridTemplateAreas: `
              "title title headline1 headline2"
              ${'"label value bar co2perm2"\n'.repeat(operationalEnergyData.length)}
            `,

            gridTemplateColumns: '17.5% 8% 1fr auto',
            alignItems: 'center',
            columnGap: getScalingSize(1),
            rowGap: getScalingSize(2),
          }}
        >
          <Text
            fontWeight="bold"
            as="h3"
            sx={{ alignSelf: 'baseline', lineHeight: 1 }}
          >
            Operational energy
          </Text>
          <Box />

          <LifecycleReportText
            lifecycleLabel="B6"
            sx={{ alignSelf: 'baseline', lineHeight: 1 }}
          />
          <Text
            as="span"
            textAlign="center"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: getScalingSize(0.5),
              whiteSpace: 'nowrap',
              alignSelf: 'baseline',
              lineHeight: 1,
            }}
          >
            <Icon
              category="costEmission"
              icon="kgCO2e"
              size={getScalingSize(1.3)}
            />{' '}
            kg CO₂e/m²
          </Text>

          {operationalEnergyData.map((item, index) => {
            const energyDisplayData = getDisplayValueData(
              {
                value: item.energyValue,
                unit: 'kWh',
              },
              { divideBy: { unit: 'year' } },
            );
            const co2ePerGfa = item.carbonValue / gfa;
            return (
              <React.Fragment key={index}>
                <Text as="p">{item.label}</Text>
                <Text as="p" fontWeight="bold">
                  {energyDisplayData.value}
                  <Text color="text.subtle" as="span">
                    &nbsp;{energyDisplayData.unit}
                  </Text>
                </Text>
                <HorizontalStackedBarChart
                  segments={item.graphData.segments}
                  totalWidthPercent={item.graphData.totalWidthPercent}
                  colorShades={shades}
                />
                <Text as="p" fontWeight="bold" textAlign="left">
                  {guards.is.finiteNumber(co2ePerGfa)
                    ? getDisplayNumber(co2ePerGfa, {
                        notation: 'compact',
                      })
                    : '-'}
                </Text>
              </React.Fragment>
            );
          })}
        </Box>

        {/* Energy Class */}
        <Box
          className={classes.energyClass}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: getScalingSize(1),
          }}
        >
          <Text fontWeight="bold" as="h5">
            Energy class
          </Text>
          <EnergyClassBar
            colorOverride={color} // for beauty
            energyClassInfo={energyClassData.energyClassInfo}
          />
          <Text as="p">
            Primary energy score:{' '}
            <Text as="span" fontWeight="bold">
              {primaryEnergyScoreData.value}
            </Text>
            <Text as="span" color="text.subtle">
              &nbsp;{primaryEnergyScoreData.unit}
            </Text>
          </Text>
        </Box>

        {/* Consumption and Generation */}
        <Box
          className={classes.consumptionSection}
          sx={{
            display: 'flex',
            gap: getScalingSize(5),
          }}
        >
          <Box
            sx={{
              flex: '0 0 35%',
              display: 'flex',
              flexDirection: 'column',
              gap: getScalingSize(3),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: getScalingSize(1),
              }}
            >
              <Text fontWeight="bold" as="h5">
                Energy Use
              </Text>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: getScalingSize(0.8),
                }}
              >
                {consumptionData.map((item) => {
                  return (
                    <EnergyRow
                      key={item.consumer}
                      label={item.label}
                      value={getDisplayValueData({
                        value: item.value,
                        unit: 'kWh',
                      })}
                      color={shades.getById(item.consumer)}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: getScalingSize(1),
              }}
            >
              <Text fontWeight="bold" as="h5">
                Generation
              </Text>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: getScalingSize(0.8),
                }}
              >
                {generationData.length === 0 && (
                  <Text as="span" color="text.subtle" fontStyle="italic">
                    No energy generation data available
                  </Text>
                )}
                {generationData.map((item) => {
                  return (
                    <EnergyRow
                      key={item.carrier}
                      label={item.label}
                      value={getDisplayValueData({
                        value: item.value,
                        unit: 'kWh',
                      })}
                      color={shades.getById(item.carrier)}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              alignSelf: 'flex-start',
              width: '30%',
            }}
          >
            <PieChart data={consumptionData} colorShades={shades} />
          </Box>
        </Box>
      </Box>
    </Page>
  );
};
